import { gql } from '@apollo/client'

export const PROVIDER_PUBLIC_PROFILE_QUERY = gql`
  query ProviderPublicProfile($id: ID!, $reviewsFirst: Int!, $reviewsAfter: String, $reviewsSortBy: ReviewSortField) {
    providerProfile(id: $id) {
      id
      bio
      yearsExperience
      languages
      hourlyRateCents
      verified
      verifiedBadge
      backgroundChecked
      instantBooking
      onVacation
      vacationUntil
      averageRating
      totalReviews
      ratingBreakdown {
        rating
        count
        percentage
      }
      completedJobs
      trustScore {
        level
        reliabilityScore
        memberSince
      }
      user {
        firstName
        lastName
        avatarUrl
      }
      serviceOfferings {
        id
        customPriceCents
        customDurationMinutes
        serviceType {
          id
          name
          nameEl
          translationKey
          basePriceCents
          estimatedDurationMinutes
          serviceCategory {
            id
            name
            nameEl
            translationKey
          }
        }
      }
      availabilitySlots {
        id
        dayOfWeek
        dayName
        startTime
        endTime
      }
      serviceAreas {
        id
        postalCode
        areaName
        city
      }
      reviews(first: $reviewsFirst, after: $reviewsAfter, sortBy: $reviewsSortBy) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          rating
          comment
          createdAt
          reviewer {
            firstName
            lastName
          }
        }
      }
    }
  }
`

export interface ProviderProfileUser {
  firstName: string
  lastName: string
  avatarUrl: string | null
}

export interface ProviderProfileCategory {
  id: string
  name: string
  nameEl: string
  translationKey: string
}

export interface ProviderProfileServiceType {
  id: string
  name: string
  nameEl: string
  translationKey: string
  basePriceCents: number
  estimatedDurationMinutes: number
  serviceCategory: ProviderProfileCategory
}

export interface ProviderProfileServiceOffering {
  id: string
  customPriceCents: number | null
  customDurationMinutes: number | null
  serviceType: ProviderProfileServiceType
}

export interface ProviderProfileAvailabilitySlot {
  id: string
  dayOfWeek: number
  dayName: string
  startTime: string
  endTime: string
}

export interface ProviderProfileServiceArea {
  id: string
  postalCode: string
  areaName: string | null
  city: string
}

export interface ProviderProfileReview {
  id: string
  rating: number
  comment: string | null
  createdAt: string
  reviewer: {
    firstName: string
    lastName: string
  }
}

export interface ProviderRatingBreakdownBucket {
  rating: number
  count: number
  percentage: number
}

export interface ProviderProfileReviewsConnection {
  totalCount: number
  pageInfo: {
    hasNextPage: boolean
    endCursor: string | null
  }
  nodes: ProviderProfileReview[]
}

export interface ProviderProfileTrustScore {
  level: string
  reliabilityScore: number
  memberSince: string
}

export interface ProviderPublicProfile {
  id: string
  bio: string | null
  yearsExperience: number | null
  languages: string[]
  hourlyRateCents: number | null
  verified: boolean
  verifiedBadge: boolean
  backgroundChecked: boolean
  instantBooking: boolean
  onVacation: boolean
  vacationUntil: string | null
  averageRating: number
  totalReviews: number
  ratingBreakdown: ProviderRatingBreakdownBucket[]
  completedJobs: number
  trustScore: ProviderProfileTrustScore
  user: ProviderProfileUser
  serviceOfferings: ProviderProfileServiceOffering[]
  availabilitySlots: ProviderProfileAvailabilitySlot[]
  serviceAreas: ProviderProfileServiceArea[]
  reviews: ProviderProfileReviewsConnection
}

export interface ProviderPublicProfileData {
  providerProfile: ProviderPublicProfile | null
}

export interface ProviderPublicProfileVariables {
  id: string
  reviewsFirst: number
  reviewsAfter?: string | null
  reviewsSortBy?: 'RECENT' | 'HIGHEST_RATING' | 'LOWEST_RATING'
}
